/**
 * Settings Module
 *
 * Handles settings page functionality including tab-specific save handlers.
 */

import { $, ready } from '../utils/dom.js';

/**
 * Settings Tab Manager
 * Manages tab-specific save handlers and button labels
 */
class SettingsTabManager {
    constructor() {
        this.handlers = {};
        this.activeTab = null;
        this.form = null;
        this.saveButton = null;
        this.defaultLabel = 'Save Settings';
    }

    /**
     * Initialize the manager
     */
    init() {
        this.form = $('#settings-form');
        this.saveButton = $('#settings-save-button');

        if (!this.saveButton) {
            return;
        }

        this.defaultLabel = this.saveButton.dataset.defaultLabel || 'Save Settings';

        // Add click handler to save button
        this.saveButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.handleSaveAction();
        });
    }

    /**
     * Set the active tab
     * @param {string} tabId - Tab identifier
     * @param {string|null} label - Optional label for button text
     */
    setActiveTab(tabId, label = null) {
        this.activeTab = tabId;
        const buttonLabel = label ? `Save ${label} Settings` : this.defaultLabel;

        if (this.saveButton) {
            this.saveButton.textContent = buttonLabel;
        }
    }

    /**
     * Register a custom save handler for a tab
     * @param {string} tabId - Tab identifier
     * @param {Function} handler - Async handler function
     */
    register(tabId, handler) {
        if (typeof handler === 'function') {
            this.handlers[tabId] = handler;
        }
    }

    /**
     * Unregister a save handler
     * @param {string} tabId - Tab identifier
     */
    unregister(tabId) {
        delete this.handlers[tabId];
    }

    /**
     * Handle save action
     * Calls registered handler or submits form
     */
    async handleSaveAction() {
        const handler = this.handlers[this.activeTab];

        if (typeof handler === 'function') {
            try {
                this.setButtonLoading(true);
                await handler();
            } catch (error) {
                console.error('Error saving settings:', error);
            } finally {
                this.setButtonLoading(false);
            }
            return;
        }

        // Default: submit the form
        if (this.form) {
            this.form.submit();
        }
    }

    /**
     * Set button loading state
     * @param {boolean} loading - Whether button is in loading state
     */
    setButtonLoading(loading) {
        if (!this.saveButton) return;

        this.saveButton.disabled = loading;
        this.saveButton.classList.toggle('opacity-75', loading);
        this.saveButton.classList.toggle('cursor-not-allowed', loading);
    }
}

// Create singleton instance
const manager = new SettingsTabManager();

/**
 * Initialize settings page
 */
function initSettings() {
    manager.init();
}

// Auto-initialize
ready(initSettings);

// Expose globally for tab handlers
window.SettingsTabManager = manager;

export default manager;
